import React, { useState } from "react"

import { useTranslation } from "react-i18next"
import { Link, useLocation } from "react-router-dom"

import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { Card, Grid, IconButton } from "@mui/material"
import { Button, Menu, MenuItem, Skeleton, Tag, Tooltip, Typography } from "@synapse-analytics/synapse-ui"

import { KonanAvatarComponent } from "../../components/core/KonanAvatar"
import { type DataFileCardProps, FileTypeColorMap } from "./interfaces"

import styles from "./DataFiles.module.scss"

export function DataFileCard(props: Readonly<DataFileCardProps>): React.ReactElement {
  const { dataFile, onDownloadInitiated, menuOptions } = props

  const { search, pathname } = useLocation()

  const { t } = useTranslation("datafiles")

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  return (
    <Card className="card-box-shadow">
      <Grid container direction="column" className={styles.card} gap={1}>
        <Grid container item xs={12} justifyContent={"space-between"}>
          <Grid item xs={10}>
            <Tag size="small" color={FileTypeColorMap[dataFile.type as keyof typeof FileTypeColorMap]}>
              {t(`datafiletypes.${dataFile.type}`)}
            </Tag>
          </Grid>

          {/* Menu  */}
          {menuOptions && menuOptions.length > 0 && (
            <Grid container item xs={2} justifyContent="flex-end" alignItems="flex-start">
              <IconButton
                style={{ marginTop: "-5px" }}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
                aria-label="settings"
                size="small"
              >
                <MoreHorizIcon sx={{ color: "var(--base-text-2)" }} />
              </IconButton>

              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} menuMaxContent>
                {menuOptions.map((option) => (
                  <MenuItem
                    onClick={() => {
                      option.onClick()
                      setAnchorEl(null)
                    }}
                    key={option.title}
                  >
                    <Typography variant="a" color={option.variant ?? "base"} variantColor={2}>
                      {option.title}
                    </Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Grid>
          )}
        </Grid>

        <Link to={`${pathname}${search}&file_id=${dataFile.uuid}`} className={styles.dataFileName}>
          {/* TODO:: should be handled from SUI, typography tooltip variant without the icon */}
          <Tooltip title={dataFile.name} placement="top" width={"100%"}>
            <Typography variant="h2-bold" className={styles.dataFileName} noWrap color="important" variantColor={2}>
              {dataFile.name}
            </Typography>
          </Tooltip>
        </Link>

        {/* Avatar  */}
        <Grid container item xs={12} justifyContent="space-between" wrap="nowrap">
          <Grid item xs={10}>
            <KonanAvatarComponent
              createdBy={dataFile.created_by}
              title={dataFile.created_by}
              createdAt={dataFile.created_at}
            />
          </Grid>

          <Grid item display="flex" alignSelf="flex-end" justifySelf="flex-end">
            <Button onClick={() => onDownloadInitiated()} size={"small"} variant="secondary">
              <FileDownloadOutlinedIcon sx={{ color: "var(--base-text-2)", fontSize: "16px" }} />
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  )
}

/**
 * Loading card for script card
 * @returns {React.ReactElement}
 */
export function DataFileLoadingCard(): React.ReactElement {
  return (
    <Card className={"card-box-shadow"}>
      <Grid container direction="column" className={styles.card} gap={1}>
        <Grid item xs={3} mb={0.5}>
          <Skeleton variant="rectangular" height={15} width={"20%"} />
        </Grid>

        <Grid item mt={1} xs={12}>
          <Skeleton variant="rectangular" height={15} width={"60%"} />
        </Grid>

        <Grid item xs={12} container flexWrap="nowrap" spacing={1} alignItems="center">
          <Grid item mt={1}>
            <Skeleton variant="circular" width={32} height={32} />
          </Grid>

          <Grid direction="column" container item mt={1}>
            <Grid item xs={6} my={1}>
              <Skeleton variant="rectangular" height={10} width={"80%"} />
            </Grid>
            <Grid item xs={3} mb={0.5}>
              <Skeleton variant="rectangular" height={10} width={"30%"} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  )
}
